import { useMemo, useState } from "react";
import type uPlot from "uplot";
import { SparklineUplot } from "@/components/SparklineUplot";
import { cn } from "@/lib/utils";

// TotalLoadChart — uPlot overlay of per-service ACUVIM load plus a total
// line, for the dyno dashboard's total-show-load section. Selectable
// services, a 3-phase / single-line toggle, and (single-line mode) a
// kVA / max-leg-A / avg-leg-A metric selector.
//
// Per-service colors come from an indexed palette below — NOT SEVERITY_COLORS
// from lib/pqTuning, which is a PQ-severity map with the wrong semantics here.
//
// Data prep (alignment, leg reduction, total) is factored into the pure
// helpers below so it can be unit-tested without mounting uPlot.

export interface TotalLoadServiceSeries {
  system_id: string;
  service_name: string | null;
  ts: number[]; // ms epoch
  current_l1: (number | null)[];
  current_l2: (number | null)[];
  current_l3: (number | null)[];
  apparent_power: (number | null)[]; // VA
}

export type PhaseMode = "3phase" | "single";
export type SingleMetric = "kva" | "maxA" | "avgA";

/** Indexed per-service line palette. Distinct from PQ SEVERITY_COLORS. */
export const SERVICE_PALETTE = [
  "#38bdf8", "#a78bfa", "#fb923c", "#4ade80",
  "#f472b6", "#facc15", "#22d3ee", "#fb7185",
];
/** Total line — prominent, neutral, so it reads above the service lines. */
export const TOTAL_COLOR = "#e2e8f0";

export function serviceColor(index: number): string {
  return SERVICE_PALETTE[index % SERVICE_PALETTE.length];
}

/** Reduce three phase legs to one value for single-line mode. Returns null
 * when every leg is null (a genuine gap) so uPlot breaks the line. */
export function reduceLegs(
  legs: (number | null)[],
  metric: "maxA" | "avgA",
): number | null {
  const present = legs.filter((x): x is number => x != null);
  if (present.length === 0) return null;
  if (metric === "maxA") return Math.max(...present);
  return present.reduce((a, b) => a + b, 0) / present.length;
}

/** The single-line value series for one service under the given metric.
 * kVA reads apparent_power (scaled VA -> kVA); the amp metrics reduce the
 * three current legs per timestamp. */
export function singleLineValues(
  svc: TotalLoadServiceSeries,
  metric: SingleMetric,
): (number | null)[] {
  if (metric === "kva") {
    return svc.apparent_power.map((va) => (va == null ? null : va / 1000));
  }
  return svc.ts.map((_, i) =>
    reduceLegs(
      [svc.current_l1[i], svc.current_l2[i], svc.current_l3[i]],
      metric,
    ),
  );
}

/** Sum a set of aligned series element-wise. A column is null only when
 * every contributing series is null there (otherwise nulls count as 0). */
export function sumAligned(serieses: (number | null)[][]): (number | null)[] {
  if (serieses.length === 0) return [];
  const len = serieses[0].length;
  const out: (number | null)[] = [];
  for (let i = 0; i < len; i++) {
    let total = 0;
    let seen = false;
    for (const s of serieses) {
      const v = s[i];
      if (v != null) {
        total += v;
        seen = true;
      }
    }
    out.push(seen ? total : null);
  }
  return out;
}

/** Realign one service's value column onto a shared sorted timestamp axis,
 * filling null where the service has no sample. */
function alignTo(
  ownTs: number[],
  values: (number | null)[],
  allTs: number[],
): (number | null)[] {
  const byTs = new Map<number, number | null>();
  for (let i = 0; i < ownTs.length; i++) byTs.set(ownTs[i], values[i] ?? null);
  return allTs.map((t) => (byTs.has(t) ? byTs.get(t)! : null));
}

export interface ChartBuild {
  data: uPlot.AlignedData;
  series: uPlot.Series[];
}

/**
 * Build uPlot data + series config. The total line always sums every
 * service; `selected` only controls which per-service lines are drawn.
 */
export function buildChartData(
  services: TotalLoadServiceSeries[],
  mode: PhaseMode,
  metric: SingleMetric,
  selected: ReadonlySet<string>,
): ChartBuild {
  // Unified, sorted timestamp axis across every service.
  const allTsSet = new Set<number>();
  for (const svc of services) for (const t of svc.ts) allTsSet.add(t);
  const allTs = [...allTsSet].sort((a, b) => a - b);
  const xs = allTs.map((t) => t / 1000); // uPlot time scale wants seconds

  const data: uPlot.AlignedData = [xs];
  const series: uPlot.Series[] = [{}]; // index 0 — implicit time series

  const LEG_LABELS = ["L1", "L2", "L3"] as const;

  if (mode === "3phase") {
    // Per-leg aligned currents for every service (needed for the total too).
    const perSvcLegs = services.map((svc) => [
      alignTo(svc.ts, svc.current_l1, allTs),
      alignTo(svc.ts, svc.current_l2, allTs),
      alignTo(svc.ts, svc.current_l3, allTs),
    ]);
    // Total: per-leg sum across all services.
    for (let leg = 0; leg < 3; leg++) {
      data.push(sumAligned(perSvcLegs.map((legs) => legs[leg])));
      series.push({
        label: `Total ${LEG_LABELS[leg]}`,
        stroke: TOTAL_COLOR,
        width: 2,
        points: { show: false },
        spanGaps: false,
      });
    }
    // Selected services: 3 lines each.
    services.forEach((svc, idx) => {
      if (!selected.has(svc.system_id)) return;
      const name = svc.service_name || svc.system_id;
      for (let leg = 0; leg < 3; leg++) {
        data.push(perSvcLegs[idx][leg]);
        series.push({
          label: `${name} ${LEG_LABELS[leg]}`,
          stroke: serviceColor(idx),
          width: 1,
          dash: leg === 1 ? [6, 3] : leg === 2 ? [2, 2] : undefined,
          points: { show: false },
          spanGaps: false,
        });
      }
    });
  } else {
    const metricLabel =
      metric === "kva" ? "kVA" : metric === "maxA" ? "max A" : "avg A";
    // Per-service single-line values, aligned.
    const perSvc = services.map((svc) =>
      alignTo(svc.ts, singleLineValues(svc, metric), allTs),
    );
    // Total: sum of every service's single-line value.
    data.push(sumAligned(perSvc));
    series.push({
      label: `Total (${metricLabel})`,
      stroke: TOTAL_COLOR,
      width: 2,
      points: { show: false },
      spanGaps: false,
    });
    services.forEach((svc, idx) => {
      if (!selected.has(svc.system_id)) return;
      data.push(perSvc[idx]);
      series.push({
        label: svc.service_name || svc.system_id,
        stroke: serviceColor(idx),
        width: 1,
        points: { show: false },
        spanGaps: false,
      });
    });
  }

  return { data, series };
}

interface TimeRangeOption {
  label: string;
  seconds: number;
}

interface TotalLoadChartProps {
  services: TotalLoadServiceSeries[];
  /** X-axis range in seconds-since-epoch. */
  range: [number, number];
  /** Render an in-area error message instead of the plot. */
  error?: boolean;
  /** Time-range buttons (15m/1h/4h/24h) rendered inline with the chart
      controls. Consolidated into one row to avoid wasted vertical space. */
  timeRanges?: ReadonlyArray<TimeRangeOption>;
  timeRangeIdx?: number;
  onTimeRangeChange?: (idx: number) => void;
}

export function TotalLoadChart({
  services, range, error,
  timeRanges, timeRangeIdx, onTimeRangeChange,
}: TotalLoadChartProps) {
  const [mode, setMode] = useState<PhaseMode>("single");
  // Metric persists across mode switches — it is hidden, not reset, in
  // 3-phase mode, so toggling back restores the operator's choice.
  const [metric, setMetric] = useState<SingleMetric>("kva");
  // Default state: total line only, no services selected.
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  const build = useMemo(
    () => buildChartData(services, mode, metric, selected),
    [services, mode, metric, selected],
  );

  function toggleService(systemId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(systemId)) next.delete(systemId);
      else next.add(systemId);
      return next;
    });
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        {/* Phase-mode toggle */}
        <div className="flex rounded-md border border-border text-xs">
          <button
            type="button"
            onClick={() => setMode("single")}
            className={cn(
              "px-2 py-1 rounded-l-md",
              mode === "single" ? "bg-accent text-accent-foreground" : "text-muted-foreground",
            )}
          >
            Single line
          </button>
          <button
            type="button"
            onClick={() => setMode("3phase")}
            className={cn(
              "px-2 py-1 rounded-r-md",
              mode === "3phase" ? "bg-accent text-accent-foreground" : "text-muted-foreground",
            )}
          >
            3-phase
          </button>
        </div>

        {/* Metric selector — hidden in 3-phase mode, the metric does not apply */}
        {mode === "single" && (
          <select
            aria-label="Single-line metric"
            value={metric}
            onChange={(e) => setMetric(e.target.value as SingleMetric)}
            className="rounded-md border border-border bg-background px-2 py-1 text-xs"
          >
            <option value="kva">kVA</option>
            <option value="maxA">Max-leg A</option>
            <option value="avgA">Avg-leg A</option>
          </select>
        )}

        {/* Time-range buttons — consolidated into this row (previously above
            the whole section) so they don't claim their own line. */}
        {timeRanges && timeRanges.length > 0 && (
          <div className="ml-auto flex rounded-md border border-border text-xs">
            {timeRanges.map((r, i) => (
              <button
                key={r.label}
                type="button"
                onClick={() => onTimeRangeChange?.(i)}
                className={cn(
                  "px-2 py-1",
                  i === 0 && "rounded-l-md",
                  i === timeRanges.length - 1 && "rounded-r-md",
                  i === timeRangeIdx
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground",
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Service selection chips */}
      {services.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {services.map((svc, idx) => {
            const on = selected.has(svc.system_id);
            return (
              <button
                key={svc.system_id}
                type="button"
                onClick={() => toggleService(svc.system_id)}
                className={cn(
                  "flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs",
                  on ? "border-border bg-accent" : "border-border/50 text-muted-foreground",
                )}
              >
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: on ? serviceColor(idx) : "transparent", border: `1px solid ${serviceColor(idx)}` }}
                />
                {svc.service_name || svc.system_id}
              </button>
            );
          })}
        </div>
      )}

      {error ? (
        <div className="flex h-[220px] items-center justify-center rounded-md border border-border text-sm text-muted-foreground">
          Load history is unavailable right now.
        </div>
      ) : (
        <SparklineUplot
          data={build.data}
          series={build.series}
          xRange={range}
          yRange="auto"
          height={220}
          /* Dark-theme axis styling — uPlot defaults to black text/grid which
             is invisible on the dyno-pwa's dark background. Same pattern
             PqActivity.tsx uses. */
          axes={[
            {
              stroke: "rgba(255,255,255,0.6)",
              grid: { stroke: "rgba(255,255,255,0.05)" },
            },
            {
              stroke: "rgba(255,255,255,0.6)",
              grid: { stroke: "rgba(255,255,255,0.05)" },
              size: 50,
            },
          ]}
          /* No built-in legend — the service-selection chips above already
             encode color + label + on/off, and uPlot's legend with N×3 rows
             in 3-phase mode wastes a lot of vertical space. */
        />
      )}
    </div>
  );
}
